"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import Button from "@/components/common/Button";
import { useBatchImpressionTracking } from "@/hooks/useImpressionTracking";
import { trackEvent } from "@/utils/analytics";

const GUIDEBOOK_URL = "https://featpaper.com/l/sMwaR4lg";
const GUIDEBOOK_ID = "direct-sourcing-guidebook";

function LeadMagnetSection() {
  const path = usePathname();

  const impressionEvents = useMemo(
    () => [{ eventName: "report_impression", params: { report_id: GUIDEBOOK_ID, cta_position: "home_lead_magnet" } }],
    [],
  );

  const ref = useBatchImpressionTracking<HTMLDivElement>(impressionEvents);

  const handleClick = () => {
    trackEvent("report_outbound_click", path, { report_id: GUIDEBOOK_ID, cta_position: "home_lead_magnet" });
  };

  return (
    <section className="mt-20 bg-white px-side py-20 tablet:py-24">
      <div ref={ref} className="mx-auto flex max-w-[920px] flex-col items-center gap-8 tablet:flex-row tablet:gap-12">
        {/* 왼쪽: 가이드북 표지 */}
        <div className="relative aspect-[1166/1654] w-full max-w-[320px] flex-shrink-0 overflow-hidden rounded-xl shadow-md tablet:w-[280px]">
          <Image
            src="/image/guidebook/cover.png"
            alt="서치라이트 숨은 인재를 찾는 다이렉트 소싱 가이드북 표지"
            fill
            className="object-cover"
            sizes="(max-width: 768px) 320px, 280px"
          />
        </div>

        {/* 오른쪽: 텍스트 + CTA */}
        <div className="flex flex-1 flex-col items-center text-center tablet:items-start tablet:text-left">
          <div className="mb-2 text-20 font-semibold text-primary">무료 가이드북</div>
          <h2 className="mb-3 text-30 font-semibold leading-[1.35] tracking-tight text-black">
            공고로는 안 오는 핵심 인재,
            <br />
            어떻게 찾을까요?
          </h2>
          <p className="mb-6 text-16 leading-[1.65] text-gray tablet:text-18">
            숨은 인재를 찾는 아웃바운드 채용 가이드북,
            <br />
            서치라이트가 실무 노하우로 정리했어요.
          </p>

          {/* What's inside */}
          <div className="mb-6 w-full max-w-[460px]">
            <p className="mb-3 text-14 font-bold uppercase tracking-wider text-primary">가이드북에 담긴 내용</p>
            <ul className="space-y-2.5 text-left text-16 leading-[1.55] text-gray tablet:text-18">
              <li>• 왜 지금 다이렉트 소싱인가 — 트렌드와 필요성 진단</li>
              <li>• 전략·발굴·컨택 3단계 실무 가이드</li>
              <li>• 실제 채용 성공·실패 사례 레슨런</li>
              <li>• 단계별 실행 체크리스트 (부록)</li>
            </ul>
          </div>

          <Button
            href={GUIDEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            className="bg-primary px-6 py-3.5 font-bold text-white"
          >
            가이드북 다운로드
          </Button>
          <p className="mt-3 text-13 text-gray">
            이미 <strong className="text-black">1,500명</strong>의 채용 리더들이 받아봤습니다
          </p>
        </div>
      </div>
    </section>
  );
}

export default LeadMagnetSection;
